// The code startAiChat puts on its ConvexError when the wismo-chat kill switch is off (LMD-1408,
// convex/threadMessages.ts). The portal owns it because the backend's convex/model.ts imports
// ./env, which the portal must not load at runtime; src/convex/threadMessages.test.ts pins the
// backend to it, so a renamed code fails a test instead of quietly showing the generic copy.
export const CHAT_DISABLED_ERROR_CODE = 'CHAT_DISABLED';

// ConvexError data carries the patient-facing line the server composed and, where it set one, a
// code. The data survives prod redaction; error.message does not, and for a ConvexError it is
// wrapped in internal request formatting. Null unless there is a non-empty line to show.
export const readChatErrorData = (
  error: unknown
): { code: string | null; message: string } | null => {
  if (!error || typeof error !== 'object' || !('data' in error)) {
    return null;
  }

  const { data } = error as { data?: unknown };

  if (!data || typeof data !== 'object') {
    return null;
  }

  const { code, message } = data as { code?: unknown; message?: unknown };

  if (typeof message !== 'string' || !message) {
    return null;
  }

  return { code: typeof code === 'string' ? code : null, message };
};

// The server's line when the kill switch refused a chat start, or null for any other failure so
// the caller keeps its own retry copy.
export const getChatUnavailableMessage = (error: unknown): string | null => {
  const data = readChatErrorData(error);

  return data?.code === CHAT_DISABLED_ERROR_CODE ? data.message : null;
};

// What the patient sees when a new chat's greeting could not be sent: the tries ran out without an
// answer from the server, or the server refused the greeting request. The persisted greeting row
// shows it too once the backend gives up on a greeting it never delivered, and for any greeting
// the backend marked error before it wrote any text (mapPromptToMessages), so one failure reads
// the same whichever of the two the patient sees. Proposed copy (LMD-2718), pending Dominique's
// wording.
export const AI_GREETING_START_FAILED_MESSAGE =
  "We couldn't start the chat. Please try again, or talk to a person on our team.";

// The kind of failure, for telemetry. Never the text: an error's message can carry a response
// body or server copy, and a thrown value that is not an Error has no class to report.
export const readErrorClass = (error: unknown): string =>
  error instanceof Error ? error.name || 'Error' : typeof error;

// What streamAiAssistantMessage throws when a POST would go out after the deadline its caller set:
// nothing was sent. A retried greeting carries one, so that a slow token renewal or Convex token
// exchange cannot push its POST past the retry window the backend's turn claim allows.
export class AiAssistantDispatchDeadlineError extends Error {
  constructor() {
    super('AI chat stream not sent: its dispatch deadline passed.');
    this.name = 'AiAssistantDispatchDeadlineError';
  }
}

// What streamAiAssistantMessage rejects with when the POST went out and no response headers arrived
// within the caller's responseTimeoutMs. The request was aborted and nothing was read, so to the
// caller it is a request that got no answer, the same as a fetch that rejected at once: a greeting
// retries it.
export class AiAssistantResponseTimeoutError extends Error {
  constructor() {
    super('AI chat stream got no response headers before its timeout.');
    this.name = 'AiAssistantResponseTimeoutError';
  }
}
